/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { exportPedido, deletePedido } from '../../../store/pedido/actions';
import { PRODUTO_PEDIDO_DELETE_ACCESS } from '../../../constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import Formulario from './Formulario';

const hasPermission = (moduleConfigurations, authData) =>
  hasModuleConfiguration(moduleConfigurations, authData, PRODUTO_PEDIDO_DELETE_ACCESS);

const rowMapper = p => ({
  id: p[0],
  data: p[1],
  tipo: p[2],
  local: p[3],
  fornecedor: p[4],
  repasseColaborador: p[5],
});

const defaultFilter = {
  inicio: moment().subtract(2, 'months').startOf('month').valueOf(),
  fim: moment().endOf('day').valueOf(),
};

const getIdFromSearch = (search) => {
  if (!search) return null;
  const nextId = new URLSearchParams(search).get('id');
  return nextId && String(nextId).trim() ? String(nextId).trim() : null;
};

const EstoquePedidosV2 = ({
  exportPedido,
  deletePedido,
  authData,
  moduleConfigurations,
  history,
  location,
}) => {
  const [isEntrada, setIsEntrada] = useState(false);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const canDelete = hasPermission(moduleConfigurations, authData);

  useEffect(() => {
    const deepLinkId = getIdFromSearch(location?.search);
    if (!deepLinkId) return;
    setId(deepLinkId);
    setOpen(true);
  }, [location?.search]);

  const clearDeepLinkId = () => {
    if (!getIdFromSearch(location?.search)) return;
    const params = new URLSearchParams(location.search);
    params.delete('id');
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  };

  const onDelete = rowId => deletePedido(rowId, () => {
    setReload(current => !current);
  });

  const onClose = isSave => {
    if (isSave) {
      setReload(current => !current);
    }
    setId(null);
    setOpen(false);
    clearDeepLinkId();
  };

  const headerActions = [
    {
      label: 'Nova Entrada',
      onClick: () => {
        setIsEntrada(true);
        setOpen(true);
      },
      startIcon: <AddIcon />,
      variant: 'contained',
    },
    {
      label: 'Nova Saída',
      onClick: () => {
        setIsEntrada(false);
        setOpen(true);
      },
      startIcon: <RemoveIcon />,
      variant: 'contained',
    },
    {
      label: 'Relatório',
      onClick: () => setOpenExport(true),
      startIcon: <DescriptionOutlinedIcon />,
      variant: 'outlined',
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Pedidos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="pedidos/filter"
          filterData={localFilter}
          refreshKey={reload}
          rowHeight={56}
          rowMapper={rowMapper}
          headerActions={headerActions}
          columns={[
            {
              field: 'data',
              headerName: 'Data do Pedido',
              width: 180,
              renderCell: ({ row }) => moment(row.data).format('DD/MM/YYYY HH:mm'),
            },
            {
              field: 'local',
              headerName: 'Local',
              flex: 1,
              minWidth: 180,
            },
            {
              field: 'tipo',
              headerName: 'Tipo',
              width: 120,
              renderCell: ({ row }) => row.tipo === 'SAIDA' ? 'Saída' : 'Entrada',
            },
            {
              field: 'fornecedor',
              headerName: 'Fornecedor',
              flex: 1,
              minWidth: 150,
              renderCell: ({ row }) => row.fornecedor || '-',
            },
            {
              field: 'repasseColaborador',
              headerName: 'Repasse Colaborador',
              flex: 1,
              minWidth: 180,
              renderCell: ({ row }) => row.repasseColaborador || '-',
            },
          ]}
          onEdit={rowId => {
            setId(rowId);
            setOpen(true);
          }}
          onDelete={onDelete}
          canDelete={() => canDelete}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          fields={[
            { field: 'useSelectOption', key: 'tipo', label: 'Tipo Pedido', options: [{ id: 'ENTRADA', nome: 'Entrada' }, { id: 'SAIDA', nome: 'Saída' }], md: 3 },
            { field: 'useProduto', md: 9, multiSelect: true },
            { field: 'useLocal', md: 6, multiSelect: true },
            { field: 'useFornecedor', md: 6, multiSelect: true },
            { field: 'useUsuarios', key: 'colaborador', label: 'Colaborador', md: 12, multiSelect: true },
          ]}
          onFilter={filterData => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={id}
          open={open}
          onClose={onClose}
          isEntrada={isEntrada}
        />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportPedido(localFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja exportar o relatório?</DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

EstoquePedidosV2.propTypes = {
  exportPedido: PropTypes.func.isRequired,
  deletePedido: PropTypes.func.isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  exportPedido,
  deletePedido,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EstoquePedidosV2));
